import {
  BACKEND_DOCUMENT_CHANNEL,
  DELIVERY_METHOD,
  DOCUMENT_CHANNEL
} from 'constants/documentDelivery'
import type { DocumentChannel } from 'constants/documentDelivery'
import type {
  BackendDocumentDeliveryPayload,
  BackendDocumentDeliveryPreferences,
  DocumentChannels,
  DocumentDeliverySession,
  DocumentKey
} from 'interfaces/documentDelivery.interface'

const toBackendChannel = (
  channel: DocumentChannel
): BackendDocumentDeliveryPreferences[keyof BackendDocumentDeliveryPreferences] =>
  channel === DOCUMENT_CHANNEL.EMAIL
    ? BACKEND_DOCUMENT_CHANNEL.DIGITAL
    : BACKEND_DOCUMENT_CHANNEL.POSTAL

const buildPreferences = (
  channels: DocumentChannels,
  deliveryMethod: DocumentDeliverySession['deliveryMethod']
): BackendDocumentDeliveryPreferences | null => {
  const getChannel = (key: DocumentKey) => {
    if (deliveryMethod === DELIVERY_METHOD.EMAIL) {
      return BACKEND_DOCUMENT_CHANNEL.DIGITAL
    }

    if (deliveryMethod === DELIVERY_METHOD.POST) {
      return BACKEND_DOCUMENT_CHANNEL.POSTAL
    }

    const channel = channels[key]
    return channel ? toBackendChannel(channel) : null
  }

  const preContractualInformation = getChannel('preContractual')
  const withdrawalNotificationMedium = getChannel('withdrawalMedium')
  const withdrawalReminder = getChannel('withdrawalReminder')
  const postContractualInformation = getChannel('postContractual')

  if (
    !preContractualInformation ||
    !withdrawalNotificationMedium ||
    !withdrawalReminder ||
    !postContractualInformation
  ) {
    return null
  }

  return {
    preContractualInformation,
    withdrawalNotificationMedium,
    withdrawalReminder,
    postContractualInformation
  }
}

// TODO CAT-4853: hydrate the session from BE values on load/resume and patch this full payload on selection changes.
export const buildDocumentDeliveryPayload = (
  session: DocumentDeliverySession,
  hasSecondDebtor: boolean
): BackendDocumentDeliveryPayload | null => {
  const { deliveryMethod, channels, isSecondSameAsFirst } = session

  if (!deliveryMethod) {
    return null
  }

  const primaryPreferences = buildPreferences(channels[1], deliveryMethod)
  if (!primaryPreferences) {
    return null
  }

  if (!hasSecondDebtor) {
    return {
      debtors: {
        primary: { documentDeliveryPreferences: primaryPreferences }
      }
    }
  }

  const secondaryFollowsPrimary =
    deliveryMethod !== DELIVERY_METHOD.PER_DOCUMENT || isSecondSameAsFirst
  const secondaryPreferences = buildPreferences(
    secondaryFollowsPrimary ? channels[1] : channels[2],
    deliveryMethod
  )

  if (!secondaryPreferences) {
    return null
  }

  return {
    debtors: {
      primary: { documentDeliveryPreferences: primaryPreferences },
      secondary: {
        documentDeliveryPreferencesFollowPrimary: secondaryFollowsPrimary,
        documentDeliveryPreferences: secondaryPreferences
      }
    }
  }
}
